import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import _ from "lodash";
import { logoutAndRedirectToLogin, reusableFetch } from "src/utils/useAxios";

function headers(token) {
  if (token) {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": token,
    };
  }

  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Whether the organisation with this ABN is already on the portal: { isNew, name }. Public, for the registration page
export const getOrganisation = createAsyncThunk(
  "organisation/get",
  async (abn, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/${abn}`,
        method: "GET",
        headers: headers(),
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// reusableFetch sends the stored token and logs the user out on a 401
export const getAllOrganisations = createAsyncThunk(
  "organisation/getAllOrganisations",
  async (_arg, { rejectWithValue }) =>
    reusableFetch("organisation/all", "GET", null, rejectWithValue)
);

// Master users approve or deactivate an organisation from the organisations page. Returns the organisation as that
// page lists it
export const updateOrganisationStatus = createAsyncThunk(
  "organisation/updateStatus",
  async ({ organisationId, status }, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/${organisationId}/status`,
        method: "POST",
        headers: headers(localStorage.token),
        data: { status },
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      // An expired session, handled as reusableFetch does
      if (e.response?.status === 401) logoutAndRedirectToLogin();
      return rejectWithValue(e.response?.data);
    }
  }
);

// Emails each person a link to register in the organisation: { invited, skipped }. Admins always invite to their own
// organisation, master users pass organisationId
export const inviteUsers = createAsyncThunk(
  "organisation/inviteUsers",
  async ({ organisationId, invites }, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/invite`,
        method: "POST",
        headers: headers(localStorage.token),
        data: { organisationId, invites },
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      // An expired session, handled as reusableFetch does
      if (e.response?.status === 401) logoutAndRedirectToLogin();
      return rejectWithValue(e.response?.data);
    }
  }
);

// What the invitation in a register link is for: { organisationName, abn, firstName, email, userType }. Public, for
// the registration page. The token is posted so it stays out of the URL the server logs
export const getInvitation = createAsyncThunk(
  "organisation/getInvitation",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/invite/details`,
        method: "POST",
        headers: headers(),
        data: { token },
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const updateAddress = createAsyncThunk(
  "address/update",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    console.log("get org", data);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/address`,
        method: "POST",
        headers: headers(token),
        data,
      }).catch((err) => {
        console.log("err", err);
      });
      // console.log("signUp response:", userResponse);
      if (response.status === "error") {
        console.log("organisation res", response);
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      console.log("Error", e.response.data);
      return rejectWithValue(e.response.data);
    }
  }
);

export const updateEmail = createAsyncThunk(
  "address/update",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    console.log("get org", data);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/email`,
        method: "POST",
        headers: headers(token),
        data,
      }).catch((err) => {
        console.log("err", err);
      });
      // console.log("signUp response:", userResponse);
      if (response.status === "error") {
        console.log("organisation res", response);
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      console.log("Error", e.response.data);
      return rejectWithValue(e.response.data);
    }
  }
);

export const updatePhoneNumber = createAsyncThunk(
  "address/update",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    console.log("get org", data);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/organisation/phone-number`,
        method: "POST",
        headers: headers(token),
        data,
      }).catch((err) => {
        console.log("err", err);
      });
      // console.log("signUp response:", userResponse);
      if (response.status === "error") {
        console.log("organisation res", response);
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      console.log("Error", e.response.data);
      return rejectWithValue(e.response.data);
    }
  }
);

const defaultState = {
  organisations: [],
  gettingOrganisations: false,
}

export const organisationSlice = createSlice({
  name: "organisation",
  initialState: defaultState,
  reducers: {
    saveValue: (state, action) => {
      console.log(action.payload);
      return _.merge(state, action.payload);
    },
    resetState: () => this.initialState,
  },
  extraReducers: {
    [getAllOrganisations.pending]: (state) => {
      state.gettingOrganisations = true;
    },
    [getAllOrganisations.fulfilled]: (state, { payload }) => {
      state.organisations = payload.data
      state.isSuccess = true;
      state.gettingOrganisations = false;
    },
    [getAllOrganisations.rejected]: (state, { payload }) => {
      state.isError = true;
      state.gettingOrganisations = false;
    },
    [updateOrganisationStatus.fulfilled]: (state, { payload }) => {
      state.organisations = state.organisations.map((organisation) =>
        organisation._id === payload.data._id ? payload.data : organisation
      );
    },

    [updateAddress.fulfilled]: (state, { payload }) => {
      console.log("payload", payload);
      state.isFetching = false;
      state.isSuccess = true;
      // const { user, token } = payload.data;
      // state.token = token;
      // state.user = user;
      state = _.merge(state, payload.data);
    },
    [updateAddress.pending]: (state) => {
      state.isFetching = true;
      // state.isCreating = true;
    },
    [updateAddress.rejected]: (state, { payload }) => {
      // console.log("payload:", payload);
      state.isFetching = false;
      state.isError = true;
      // state.errorMessage = payload.message;
    },

    [updateEmail.fulfilled]: (state, { payload }) => {
      console.log("payload", payload);
      state.isFetching = false;
      state.isSuccess = true;
      // const { user, token } = payload.data;
      // state.token = token;
      // state.user = user;
      state = _.merge(state, payload.data);
    },
    [updateEmail.pending]: (state) => {
      state.isFetching = true;
      // state.isCreating = true;
    },
    [updateEmail.rejected]: (state, { payload }) => {
      // console.log("payload:", payload);
      state.isFetching = false;
      state.isError = true;
      // state.errorMessage = payload.message;
    },

    [updatePhoneNumber.fulfilled]: (state, { payload }) => {
      console.log("payload", payload);
      state.isFetching = false;
      state.isSuccess = true;
      // const { user, token } = payload.data;
      // state.token = token;
      // state.user = user;
      state = _.merge(state, payload.data);
    },
    [updatePhoneNumber.pending]: (state) => {
      state.isFetching = true;
      // state.isCreating = true;
    },
    [updatePhoneNumber.rejected]: (state, { payload }) => {
      // console.log("payload:", payload);
      state.isFetching = false;
      state.isError = true;
      // state.errorMessage = payload.message;
    },
  },
});

export const { saveValue } = organisationSlice.actions;

// export const store = configureStore({
//   reducer: {
//     applications: applicationsSlice.reducer,
//   },
// });

export const organisationSelector = (state) => state.organisation;
